import {useEffect, useRef, useState} from "react";
import {usePersonalizationDraft} from "./usePersonalizationDraft";
import {
	successAttachFailure, successComposerSeed, successEntryPress, successPersonalizationAction,
	successPersonalizationEntry,
	type SuccessPersonalizationAction, type SuccessPersonalizationStage, type SuccessRefusalNotice,
} from "./successPersonalization";
import {
	attachSentPersonalization, readSentPersonalizationWindow, type SentPersonalizationWindow,
} from "./SuccessPersonalizationApi";
import type {PersonalizationDraftRequest, PersonalizationRung} from "./personalizationDraft";
import {personalizationEvents} from "./personalizationEvents";
import {lookSentWith, type SenderCompany} from "sender/senderChoice";
import {personalizationRefusalOf} from "api/apiErrorIds";

/**
 * The success screen's late look (ui#2374). Every plan includes the look (kitecorp/transferify#525), so a
 * look committed here is attached as composed, with no plan step.
 *
 * `attached` is the look the SEND itself carried, or null for a transfer that went out plain (ui#2411).
 * This composer is a fresh one and knows nothing of the draft the create panel held, so it is told.
 *
 * `company` is the company the transfer went out as, or null for a person (kitecorp/transferify-ui#3186). A look
 * attached to a company's transfer goes without its name, by the rule the send itself followed (`lookSentWith`), so
 * a name typed or inherited here never takes the company's place on the download page.
 */
export function useSuccessPersonalization(
	transferId: string, attached: PersonalizationDraftRequest | null, company: SenderCompany | null,
) {
	const [open, setOpen] = useState(false);
	const [stage, setStage] = useState<SuccessPersonalizationStage>("idle");
	// What this screen last got the api to accept. State rather than a ref: the panel renders off it,
	// and it is the rung of what was SAVED — never of what the composer happens to hold now.
	const [savedRung, setSavedRung] = useState<PersonalizationRung | null>(null);
	const [sendWindow, setSendWindow] = useState<SentPersonalizationWindow | null>(null);
	const vm = usePersonalizationDraft(true);
	// Why the last attach was REFUSED by the api (`successAttachFailure`), or null: the view says why instead of
	// offering a retry of the same snapshot.
	const [refusal, setRefusal] = useState<SuccessRefusalNotice | null>(null);
	const inFlight = useRef(false);
	const mounted = useRef(true);
	// Retained through ambiguous transport failures, so a retry re-sends the SAME draft: an attach that
	// may already have landed must not come back as a different one. Cleared the moment the api confirms
	// the save, because there is then nothing ambiguous left to replay — and because a free look may be
	// edited again, so the next press has to open the builder rather than re-send this.
	const submittedAttach = useRef<{request: PersonalizationDraftRequest; rung: PersonalizationRung} | null>(null);
	useEffect(() => {
		mounted.current = true;
		void readSentPersonalizationWindow(transferId).then(value => {
			if (mounted.current) setSendWindow(value);
		}).catch(() => { if (mounted.current) setStage("failed"); });
		return () => { mounted.current = false; };
	}, [transferId]);

	/**
	 * Re-reads the persisted window and answers the transfer it admits, or null.
	 *
	 * The WINDOW rather than a boolean, because the attach below needs the raw uuid it carries: the
	 * owner's transfer list is the one place the encoded share id this screen holds is joined to the
	 * persisted id the attach route is addressed by.
	 */
	const refreshAdmission = async (): Promise<SentPersonalizationWindow | null> => {
		const value = await readSentPersonalizationWindow(transferId);
		if (!mounted.current) return null;
		setSendWindow(value);
		return value !== null && successPersonalizationEntry({transferId,
			personalized: false, expiresAtMs: value.expiresAtMs, now: Date.now()}) ? value : null;
	};

	useEffect(() => {
		if (stage !== "submitted" || inFlight.current) return;
		const action: SuccessPersonalizationAction = submittedAttach.current === null
			? successPersonalizationAction({request: lookSentWith(company, vm.request), rung: vm.rung, availability: vm.availability,
				pending: vm.loading, blocked: vm.blocksSend})
			: {kind: "attach", ...submittedAttach.current};
		if (action.kind !== "attach") {
			if (action.kind === "empty") setStage("idle");
			// A refused draft goes BACK to the builder (ui#2416). It was folded into `pending` here, which
			// left the flow holding "submitted" with nothing to submit, and into `empty` in the rule, which
			// returned it to idle and said nothing: either way the sender pressed Done and watched the
			// screen not change. The builder is where the refusal is stated and where the fix is.
			if (action.kind === "blocked") { setStage("idle"); setOpen(true); }
			// `wait` keeps the stage: the effect runs again when the offer answers.
			return;
		}
		const {request, rung} = action;
		inFlight.current = true;
		void (async () => {
			try {
				const admitted = await refreshAdmission();
				if (admitted === null) {
					if (mounted.current) setStage("expired");
					return;
				}
				submittedAttach.current = {request, rung};
				setStage("saving");
				// The dedicated attach route (api#1592), addressed by the raw uuid the window carries —
				// not the hosted upload's fileless completion, which a peer-to-peer transfer has none of.
				const outcome = await attachSentPersonalization(admitted.transferUuid, request);
				if (!mounted.current) return;
				if (outcome !== "attached") { setStage("expired"); return; }
				setSavedRung(rung);
				// Confirmed, so there is nothing ambiguous left to replay. Dropping the retained snapshot
				// is what lets the entry reopen the BUILDER after a free save (ui#2407) instead of
				// re-sending the look that was already accepted.
				submittedAttach.current = null;
				setStage("saved");
			} catch (error) {
				if (!mounted.current) return;
				const failure = successAttachFailure(personalizationRefusalOf(error));
				// A refused snapshot is not kept: re-sending it would only be refused again (`successEntryPress`
				// then reopens the builder instead of replaying).
				if (failure.kind === "refused") submittedAttach.current = null;
				setRefusal(failure.kind === "refused" ? failure.notice : null);
				setStage("failed");
			} finally {
				inFlight.current = false;
			}
		})();
	}, [stage, vm, transferId, company]);

	/**
	 * The send's own words, put into this composer the first time it is OPENED.
	 *
	 * A replacement rather than a merge is what the api performs, so a sender who adds a look here must
	 * send their name and note along with it or lose them; `successComposerSeed` is the rule for what
	 * carries. Once only, so a sender who deletes the name does not get it back on the next open: this is
	 * where the composer STARTS, not a value it is held to.
	 *
	 * At the OPEN rather than on mount, because the composer is mounted behind the success card from the
	 * moment the card appears. Filling a sheet nobody has asked for puts its live miniature to work
	 * typesetting words that are not on screen, and that measurably moved the card the invite tag hangs
	 * from - three of `transfer-auth.spec.ts`'s Romanian journeys failed their peel-tag geometry with a
	 * seed-on-mount and pass with this.
	 */
	const seeded = useRef(false);
	const seedFromTheSend = () => {
		if (seeded.current) return;
		seeded.current = true;
		const seed = successComposerSeed(attached);
		if (seed === null) return;
		vm.setDisplayName(seed.displayName);
		vm.setMessage(seed.message);
	};

	return {
		vm, open, stage, savedRung, sendWindow, refusal,
		busy: stage === "submitted" || stage === "saving",
		show: () => {
			switch (successEntryPress({stage, unconfirmedAttach: submittedAttach.current !== null})) {
				case "replay": setStage("submitted"); return;
				case "compose": break;
			}
			setStage("idle");
			setRefusal(null);
			seedFromTheSend();
			setOpen(true);
			personalizationEvents.builderOpened("LATE");
		},
		close: () => setOpen(false),
		commit: () => { setStage("submitted"); personalizationEvents.builderCompleted("LATE"); },
	};
}
